import React, { useContext, useEffect, useRef, useState } from "react";
import {
  audioSupported,
  audioNow,
  playClick,
  playNote,
  notesFor,
  startChart,
} from "../../lib/keyboardPlan";
import {
  buildChart,
  songChart,
  chartSongs,
  timedBarResult,
  timedRestBarResult,
  timedRunSummary,
  REST,
  CHART_XP,
  beatMsToBpm,
} from "../../lib/numberSenseData";
import {
  Icon,
  Piano,
  MidiBadge,
  TypingHint,
  useTypingPiano,
  AudioSettingsContext,
} from "../numberSenseUi";
import useMidi from "./useMidi";

/* ============================================================
   Number Sense — chart mode (#87): the band simulation.
   A numbers-only chart in a key the player didn't pick; count-in,
   the rhythm section starts, and the whole form is graded bar by
   bar (right chord, on time) with timedBarResult — each bar in its
   own key, because mid-chart key changes are announced a bar ahead
   and the transposition IS the drill.

   The screen owns the transport (startChart + the audio clock);
   everything it grades with is pure and lives in numberSenseData.
   Results feed mastery through onResults; a passed run awards
   CHART_XP through onAward — the same contract as the games.
   ============================================================ */
const CAN_PLAY = audioSupported();

const ChartScreen = ({ unlocked = null, onBack, onAward, onResults }) => {
  const audioCfg = useContext(AudioSettingsContext);
  const songs = chartSongs(unlocked);

  const [chart, setChart] = useState(() => buildChart({ unlocked }));
  const [songIdx, setSongIdx] = useState(-1); // -1 = random chart
  const [phase, setPhase] = useState("ready"); // ready | play | done
  const [curBar, setCurBar] = useState(-1);
  const [marks, setMarks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [built, setBuilt] = useState(() => new Set());

  const strikesRef = useRef([]);
  const marksRef = useRef([]);
  const grooveRef = useRef(null);
  const countInRef = useRef(null);

  const stopAll = () => {
    if (countInRef.current) {
      clearTimeout(countInRef.current);
      countInRef.current = null;
    }
    if (grooveRef.current) {
      grooveRef.current.stop();
      grooveRef.current = null;
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopAll(), []);

  const reset = (nextChart, nextSongIdx) => {
    stopAll();
    strikesRef.current = [];
    marksRef.current = [];
    setMarks([]);
    setSummary(null);
    setCurBar(-1);
    setBuilt(new Set());
    setPhase("ready");
    if (nextChart) setChart(nextChart);
    if (nextSongIdx != null) setSongIdx(nextSongIdx);
  };

  const newChart = () => reset(buildChart({ unlocked }), -1);
  const nextSong = () => {
    if (!songs.length) return;
    const i = (songIdx + 1) % songs.length;
    reset(songChart(songs[i]), i);
  };

  const start = () => {
    if (!chart || phase === "play") return;
    strikesRef.current = [];
    marksRef.current = [];
    setMarks([]);
    setCurBar(-1);
    setBuilt(new Set());
    setPhase("play");
    const beatMs = audioCfg.tempo;
    for (let i = 0; i < 4; i += 1) playClick((i * beatMs) / 1000, i === 0);
    countInRef.current = window.setTimeout(() => {
      countInRef.current = null;
      grooveRef.current = startChart({
        bars: chart.bars,
        bpm: beatMsToBpm(beatMs),
      });
    }, 4 * beatMs);
  };

  // Close bars against the audio clock — the same windowing as the lesson
  // runtime's timed transport, but each bar grades in its own key.
  useEffect(() => {
    if (phase !== "play" || !chart) return undefined;
    const totalBars = chart.bars.length;
    const id = setInterval(() => {
      const g = grooveRef.current;
      if (!g) return; // still counting in
      const el = audioNow() - g.startTime;
      if (el < 0) return;
      const beatSec = g.barDur / 4;
      const bar = Math.min(Math.floor(el / g.barDur), totalBars);
      let closedAny = false;
      while (marksRef.current.length < bar) {
        const i = marksRef.current.length;
        const b = chart.bars[i];
        const start_ = g.startTime + i * g.barDur;
        const end = start_ + g.barDur;
        const strikes = strikesRef.current.filter(
          (s) => s.t >= start_ - beatSec / 2 && s.t < end
        );
        marksRef.current.push(
          b.deg === REST
            ? timedRestBarResult(strikes, start_)
            : timedBarResult(strikes, notesFor(b.key, b.deg) || [], start_, beatSec)
        );
        closedAny = true;
      }
      if (closedAny) {
        setMarks([...marksRef.current]);
        setBuilt(new Set());
      }
      if (bar >= totalBars) {
        stopAll();
        const sum = timedRunSummary(marksRef.current);
        setSummary(sum);
        // every bar exercised reading + playing its chord in its key
        const results = marksRef.current.map((m, i) => {
          const b = chart.bars[i];
          return b.deg === REST
            ? null
            : {
                concept: `${b.key}:${b.deg}`,
                correct: m.hit,
                fast: m.onTime,
                ms: m.latency != null ? Math.round(m.latency * 1000) : null,
                type: "chart",
              };
        });
        if (onResults) onResults(results.filter(Boolean));
        if (sum.passed && onAward) onAward(CHART_XP);
        setPhase("done");
        return;
      }
      setCurBar(bar);
    }, 60);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, chart]);

  // Every input path lands strikes on the audio clock (MIDI, glass, typing).
  const handleKey = (k) => {
    if (phase !== "play") return;
    strikesRef.current.push({ name: k.name, t: audioNow() });
    setBuilt((prev) => new Set(prev).add(k.name));
  };
  const midi = useMidi(handleKey);
  useTypingPiano((k) => {
    playNote(k.midi);
    handleKey(k);
  }, phase === "play");

  if (!chart) {
    // nothing playable yet (fresh account) — the menu gates this, but be safe
    return (
      <div className="ns-root">
        <div className="ns-app">
          <div className="subbar">
            <button className="iconbtn" onClick={onBack} aria-label="back">
              <Icon name="back" />
            </button>
            <div className="subbar-title">chart mode</div>
          </div>
          <div className="scroll">
            <p className="ns-stat-note">
              unlock a key's progressions first — then the band will call a chart.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // group the flat bars back under their sections for display
  const groups = chart.sections.map((s, si) => ({
    ...s,
    bars: chart.bars.map((b, i) => ({ ...b, i })).filter((b) => b.sec === si),
  }));
  const playing = phase === "play";
  const call = playing && curBar >= 0 ? chart.bars[curBar]?.call : null;
  const hasChange = chart.keys.length > 1;

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">chart mode</div>
        </div>
        <div className="qarea">
          <div className="qkind">read the chart, play it in time</div>
          <div className="qprompt">
            {chart.title ? (
              <>
                <b>{chart.title}</b>{" "}
                <span className="muted">· {chart.artist} · in {chart.key}</span>
              </>
            ) : (
              <>
                cold read — <b>in {chart.key}</b>{" "}
                {hasChange && (
                  <span className="muted">· watch for the key change</span>
                )}
              </>
            )}
          </div>
          {phase === "ready" && (
            <div className="qprompt-sub muted">
              numbers only, one chord per bar — the count-in starts the band,
              and the band doesn't stop
            </div>
          )}
          {call && (
            <div className="ns-chart-call">
              <Icon name="swap" />
              <span>
                key change — now in <b>{call}</b>
              </span>
            </div>
          )}
          <div className="ns-chart">
            {groups.map((s, si) => (
              <div className="ns-chart-sec" key={si}>
                <div className="ns-chart-head">
                  <span className="name">{s.name}</span>
                  <span className="key mono">in {s.key}</span>
                </div>
                <div className="ns-prog">
                  {s.bars.map((b) => {
                    const mark = marks[b.i];
                    const markCls = mark
                      ? mark.onTime
                        ? " hit"
                        : mark.hit
                        ? " late"
                        : " miss"
                      : "";
                    const cur = playing && b.i === curBar;
                    return (
                      <div
                        className={`ns-prog-step ${cur ? "current" : ""}${markCls}`}
                        key={b.i}
                      >
                        <span className="mono num">
                          {b.deg === REST ? "—" : b.deg}
                        </span>
                        {b.call && <span className="chord">→ {b.call}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <Piano pressed={built} onPress={handleKey} hint={[]} disabled={!playing} />
          <TypingHint midiStatus={midi.status} />
          <MidiBadge status={midi.status} devices={midi.devices} />
        </div>
        {phase === "ready" && (
          <div className="foot">
            {songs.length > 0 && (
              <button className="btn ghost" onClick={nextSong}>
                {songIdx < 0 ? "play a song form" : "next song"}
              </button>
            )}
            <button className="btn ghost" onClick={newChart}>
              another chart
            </button>
            <button className="btn" onClick={start}>
              Count it in
            </button>
            {!CAN_PLAY && (
              <div className="verdict">
                <small>heads up: this browser can't drive the band's audio</small>
              </div>
            )}
          </div>
        )}
        {phase === "play" && (
          <div className="foot">
            <div className="verdict">
              <small>
                {grooveRef.current
                  ? `bar ${Math.max(1, curBar + 1)} of ${chart.bars.length} — stay with the band`
                  : "four count-in clicks, then you're on"}
              </small>
            </div>
          </div>
        )}
        {phase === "done" && summary && (
          <div className={`foot ${summary.passed ? "right" : "wrong"}`}>
            <div className="verdict">
              <b>{summary.passed ? "you read the chart" : "keep your place"}</b>
              <small>
                {`${summary.onTime}/${summary.total} bars on the beat`}
                {summary.avgLatencyMs != null &&
                  ` · landed ~${summary.avgLatencyMs}ms after the downbeat`}
                {summary.passed && ` · +${CHART_XP} XP`}
              </small>
            </div>
            <button className="btn ghost" onClick={() => reset()}>
              Run it again
            </button>
            <button className="btn" onClick={newChart}>
              New chart
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartScreen;
